/**
 * entity-linker-registry.ts
 *
 * Pluggable, decentralized entity linker registry for the Sovereign Mesh Shell.
 *
 * Enforces:
 *   1. Declarative, untrusted-plugin proof definitions (shell owns rendering)
 *   2. Longest-prefix collision resolution precedence
 *   3. ReDoS protection (bounded regex evaluation, max input length)
 *   4. Strict boundaries rejecting CSS hex codes (#ffffff), URL fragments, and hashtags
 *   5. Unicode confusable normalization and zero-width character stripping
 */

export interface EntityReference {
  readonly meshId: string;
  readonly workspaceId: string;
  readonly pluginId: string;
  readonly entityType: 'task' | 'deal' | 'lead' | 'user' | 'wiki' | string;
  readonly entityId: string;
  readonly displayToken: string;
  readonly entityRevision?: string;
  readonly title?: string;
  readonly status?: string;
}

export interface TypeaheadSuggestion {
  readonly id: string;
  readonly displayLabel: string;
  readonly token: string;
  readonly secondaryText?: string;
  readonly category?: string;
  readonly icon?: string;
}

export interface EntityLinkDefinition {
  readonly pluginId: string;
  readonly entityType: string;
  readonly prefix: string;
  readonly tokenPattern: RegExp;
  readonly priority: number; // Higher number evaluated first (longest-prefix resolution)
  readonly visibility: 'public' | 'workspace' | 'private';
  readonly resolve: (id: string, scope: { meshId: string; workspaceId: string }) => Promise<EntityReference | null>;
  readonly typeaheadProvider?: (query: string, scope: { meshId: string; workspaceId: string }) => Promise<TypeaheadSuggestion[]>;
}

export interface ParsedEntityMatch {
  readonly start: number;
  readonly end: number;
  readonly rawMatch: string;
  readonly extractedId: string;
  readonly definition: EntityLinkDefinition;
}

export class EntityLinkerRegistry {
  private readonly definitions: EntityLinkDefinition[] = [];

  constructor() {
    this.registerDefaultLinkers();
  }

  /**
   * Registers a plugin's declarative entity linker.
   */
  register(definition: EntityLinkDefinition): void {
    this.definitions.push(definition);
    // Keep sorted by priority descending (longest / most specific prefixes first)
    this.definitions.sort((a, b) => b.priority - a.priority);
  }

  /**
   * Unregisters all definitions belonging to a plugin (e.g. when disabled).
   */
  unregister(pluginId: string): void {
    const idx = this.definitions.findIndex((d) => d.pluginId === pluginId);
    if (idx !== -1) {
      this.definitions.splice(idx, 1);
    }
  }

  /**
   * Returns all active definitions.
   */
  getDefinitions(): readonly EntityLinkDefinition[] {
    return this.definitions;
  }

  /**
   * Scans text for all valid entity tokens with boundary verification and ReDoS protection.
   */
  parseEntities(text: string): ParsedEntityMatch[] {
    if (!text || text.length > 50000) return [];

    // Normalize Unicode & strip zero-width / bidi control characters
    const normalized = text
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E]/g, '');

    const matches: ParsedEntityMatch[] = [];
    const occupiedRanges: Array<{ start: number; end: number }> = [];

    // Evaluate in priority order (longest-prefix wins)
    for (const def of this.definitions) {
      const regex = new RegExp(def.tokenPattern.source, def.tokenPattern.flags);
      let m: RegExpExecArray | null;

      while ((m = regex.exec(normalized)) !== null) {
        // Group 1 is typically leading boundary/whitespace, Group 2 is the token, Group 3 is the ID
        const rawMatch = m[0];
        const leadingWhitespace = m[1] || '';
        const matchStart = m.index + leadingWhitespace.length;
        const matchEnd = m.index + rawMatch.length;
        const extractedId = m[2] || m[1] || rawMatch;

        // Ensure this range does not overlap with higher-priority matched ranges
        const overlaps = occupiedRanges.some(
          (r) => (matchStart >= r.start && matchStart < r.end) || (matchEnd > r.start && matchEnd <= r.end),
        );

        if (!overlaps) {
          occupiedRanges.push({ start: matchStart, end: matchEnd });
          matches.push({
            start: matchStart,
            end: matchEnd,
            rawMatch: normalized.substring(matchStart, matchEnd),
            extractedId,
            definition: def,
          });
        }
      }
    }

    // Sort matches by start position in text
    return matches.sort((a, b) => a.start - b.start);
  }

  /**
   * Queries active typeahead providers matching trigger character ('#' or '@').
   */
  async getTypeaheadSuggestions(
    trigger: string,
    query: string,
    scope: { meshId: string; workspaceId: string },
    signal?: AbortSignal,
  ): Promise<TypeaheadSuggestion[]> {
    const matchingDefs = this.definitions.filter(
      (d) => d.prefix === trigger && d.typeaheadProvider !== undefined,
    );

    if (matchingDefs.length === 0) return [];

    const suggestions: TypeaheadSuggestion[] = [];

    for (const def of matchingDefs) {
      if (signal?.aborted) return [];
      try {
        const results = await Promise.race([
          def.typeaheadProvider!(query, scope),
          new Promise<TypeaheadSuggestion[]>((_, reject) =>
            setTimeout(() => reject(new Error('Provider timeout')), 400),
          ),
        ]);
        if (!signal?.aborted && results) {
          suggestions.push(...results);
        }
      } catch {
        // Fail open per-provider on timeout or error
      }
    }

    return suggestions.slice(0, 10);
  }

  /**
   * Registers default shell entity linkers on boot.
   */
  private registerDefaultLinkers(): void {
    // 1. CRM Deals (Priority 100 - Longest prefix)
    // Matches @deal-123, deal:123, #deal-123, $DEAL-123, DEAL-123
    this.register({
      pluginId: 'crm-plugin',
      entityType: 'deal',
      prefix: '@',
      // Group 1: boundary/delimiter, Group 2: extracted ID
      tokenPattern: /(^|[\s(\[*$])(?:@deal-|deal:|#deal-|\$DEAL-|DEAL-)([a-zA-Z0-9_-]{1,32})(?=[.,;:!?)\s\]*]|$|\b)/gi,
      priority: 100,
      visibility: 'workspace',
      resolve: async (id, scope) => {
        return {
          meshId: scope.meshId,
          workspaceId: scope.workspaceId,
          pluginId: 'crm-plugin',
          entityType: 'deal',
          entityId: id,
          displayToken: `@deal-${id}`,
          title: `Deal #${id}`,
          status: 'active',
        };
      },
      typeaheadProvider: async (query) => {
        const clean = query.toLowerCase().replace(/^deal-?/, '');
        const demoDeals = [
          { id: '101', title: 'Enterprise Mesh Pilot', amount: '$45,000', stage: 'Proposal' },
          { id: '102', title: 'Sovereign Node License', amount: '$12,500', stage: 'Negotiation' },
          { id: '103', title: 'Global Gov Deployment', amount: '$180,000', stage: 'Discovery' },
        ];
        return demoDeals
          .filter(
            (d) =>
              !clean ||
              query.toLowerCase().startsWith('deal') ||
              d.id.includes(clean) ||
              d.title.toLowerCase().includes(clean),
          )
          .map((d) => ({
            id: d.id,
            displayLabel: `@deal-${d.id} — ${d.title}`,
            token: `@deal-${d.id}`,
            secondaryText: `${d.amount} · ${d.stage}`,
            category: 'CRM Deal',
            icon: '💼',
          }));
      },
    });

    // 2. CRM Leads (Priority 90)
    // Matches @lead-123 or lead:123
    this.register({
      pluginId: 'crm-plugin',
      entityType: 'lead',
      prefix: '@',
      tokenPattern: /(^|[\s(\[*$])(?:@lead-|lead:|#lead-)([a-zA-Z0-9_-]{1,32})(?=[.,;:!?)\s\]*]|$|\b)/gi,
      priority: 90,
      visibility: 'workspace',
      resolve: async (id, scope) => {
        return {
          meshId: scope.meshId,
          workspaceId: scope.workspaceId,
          pluginId: 'crm-plugin',
          entityType: 'lead',
          entityId: id,
          displayToken: `@lead-${id}`,
          title: `Lead #${id}`,
          status: 'active',
        };
      },
    });

    // 3. Kanban Sprint Tasks (Priority 80)
    // Matches #88, #92, #104, #task-104 (Strict negative boundary: ignores CSS hex like #ffffff or trailing words)
    this.register({
      pluginId: 'kanban-plugin',
      entityType: 'task',
      prefix: '#',
      // Group 1: boundary/delimiter, Group 2: extracted numeric/task ID
      tokenPattern: /(^|[\s(\[*])#(?:task-)?(\d{1,6})(?![a-fA-F0-9]{3,6})(?=[.,;:!?)\s\]*]|$|\b)/g,
      priority: 80,
      visibility: 'workspace',
      resolve: async (id, scope) => {
        return {
          meshId: scope.meshId,
          workspaceId: scope.workspaceId,
          pluginId: 'kanban-plugin',
          entityType: 'task',
          entityId: id,
          displayToken: `#${id}`,
          title: `Task #${id}`,
          status: 'in_progress',
        };
      },
      typeaheadProvider: async (query) => {
        const q = query.toLowerCase();
        const demoTasks = [
          { id: '88', title: 'Implement WebSocket Signalling Relay', status: 'in_progress', estimate: '2d' },
          { id: '92', title: 'Database Staging Schema Migration', status: 'blocked', estimate: '1d' },
          { id: '104', title: 'AAL3 Step-Up WebAuthn Modal', status: 'to_do', estimate: '3d' },
          { id: '110', title: 'Export W3C Verifiable Credentials', status: 'done', estimate: '1d' },
        ];
        return demoTasks
          .filter((t) => t.id.includes(q) || t.title.toLowerCase().includes(q))
          .map((t) => ({
            id: t.id,
            displayLabel: `#${t.id} — ${t.title}`,
            token: `#${t.id}`,
            secondaryText: `Status: ${t.status} · ${t.estimate}`,
            category: 'Sprint Task',
            icon: '📋',
          }));
      },
    });

    // 4. LLMWiki Runbook Documents (Priority 70)
    // Matches wiki://devops/db-access
    this.register({
      pluginId: 'wiki-plugin',
      entityType: 'wiki',
      prefix: 'w',
      tokenPattern: /(?:^|\s)(wiki:\/\/[a-zA-Z0-9\-_/]{1,200})\b/gi,
      priority: 70,
      visibility: 'workspace',
      resolve: async (id, scope) => {
        return {
          meshId: scope.meshId,
          workspaceId: scope.workspaceId,
          pluginId: 'wiki-plugin',
          entityType: 'wiki',
          entityId: id,
          displayToken: id,
          title: 'LLMWiki Runbook Reference',
        };
      },
    });

    // 5. Workspace Directory Users (Priority 10 - Lower priority than @deal- or @lead-)
    // Matches @alice or hns://alice.mesh
    this.register({
      pluginId: 'directory-plugin',
      entityType: 'user',
      prefix: '@',
      tokenPattern: /(?:^|\s)@([a-zA-Z0-9._-]{2,32})\b/gi,
      priority: 10,
      visibility: 'public',
      resolve: async (id, scope) => {
        return {
          meshId: scope.meshId,
          workspaceId: scope.workspaceId,
          pluginId: 'directory-plugin',
          entityType: 'user',
          entityId: id,
          displayToken: `@${id}`,
          title: `@${id}`,
        };
      },
      typeaheadProvider: async (query) => {
        const q = query.toLowerCase();
        const demoUsers = [
          { id: 'alice', name: 'Alice Sovereign', role: 'SecOps Lead', handle: 'alice.mesh' },
          { id: 'bob', name: 'Bob Engineer', role: 'Core Kernel Dev', handle: 'bob.mesh' },
          { id: 'herxq', name: 'Peer herxq', role: 'Workspace Member', handle: 'herxq.mesh' },
        ];
        return demoUsers
          .filter((u) => u.id.toLowerCase().includes(q) || u.name.toLowerCase().includes(q))
          .map((u) => ({
            id: u.id,
            displayLabel: `@${u.id} (${u.name})`,
            token: `@${u.id}`,
            secondaryText: `${u.role} · ${u.handle}`,
            category: 'Team Member',
            icon: '👤',
          }));
      },
    });

    // 6. Sovereign Mesh Identity URIs (Priority 15 - Matches hxn://... or legacy hns://...)
    this.register({
      pluginId: 'directory-plugin',
      entityType: 'user',
      prefix: 'hxn://',
      tokenPattern: /(?:^|\s)((?:hxn|hns):\/\/[a-zA-Z0-9._~*-]+(?:\/[a-zA-Z0-9._~*-]+)*)/gi,
      priority: 15,
      visibility: 'public',
      resolve: async (uri, scope) => {
        const canonical = uri.trim().replace(/^hns:\/\//i, 'hxn://');
        return {
          meshId: scope.meshId,
          workspaceId: scope.workspaceId,
          pluginId: 'directory-plugin',
          entityType: 'user',
          entityId: canonical,
          displayToken: canonical,
          title: canonical,
        };
      },
    });
  }
}

export const globalEntityLinkerRegistry = new EntityLinkerRegistry();
